import { FormEvent, useState } from "react";
import { requestOtp, verifyOtp } from "../api";
import { pushEvent } from "../gtm";
import type { QuoteData, QuoteResult } from "../types";

interface Props {
  partner: string;
  quoteId: string;
  quoteData: QuoteData;
  onDone: (result: QuoteResult) => void;
}

type SubStep = "phone" | "code";

export default function Step2Phone({ partner, quoteId, quoteData, onDone }: Props) {
  const [subStep, setSubStep] = useState<SubStep>("phone");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [consentTerms, setConsentTerms] = useState(false);
  const [consentContact, setConsentContact] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cityLabel = quoteData.mode === "warszawa" ? "Warszawa" : quoteData.city_name ?? "";
  const addressLabel = `${quoteData.street} ${quoteData.house_number}, ${cityLabel}`;

  const codeDisabled = subStep === "phone";

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (subStep === "phone") {
      if (!consentTerms) {
        setError("Zaakceptuj Politykę prywatności, aby kontynuować");
        return;
      }
      if (!consentContact) {
        setError("Zgoda na kontakt telefoniczny jest wymagana");
        return;
      }
      if (phone.replace(/\D/g, "").length < 9) {
        setError("Podaj poprawny numer telefonu");
        return;
      }
      setLoading(true);
      try {
        await requestOtp(partner, quoteId, phone, consentTerms, consentContact);
        setSubStep("code");
        pushEvent("sms_sent", { partner });
      } catch (err) {
        setError(err instanceof Error ? err.message : "Nie udało się wysłać SMS");
      } finally {
        setLoading(false);
      }
      return;
    }

    // subStep === "code"
    if (!/^\d{4,8}$/.test(code)) {
      setError("Wpisz kod z SMS (4–8 cyfr)");
      return;
    }
    setLoading(true);
    try {
      const result = await verifyOtp(partner, quoteId, code);
      onDone(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Niepoprawny kod");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-5">
      <header>
        <h2 className="text-xl font-bold text-brand">Wycena gotowa!</h2>
        <p className="text-sm text-gray-600 mt-1">
          Mamy wycenę dla <strong>{quoteData.area_m2} m²</strong>, {addressLabel}. Potrzebujemy
          zweryfikować, że jesteś człowiekiem — wyślemy krótki kod na telefon.
        </p>
      </header>

      <div className="bg-brand-50 border-2 border-dashed border-brand-100 rounded-lg p-6 text-center">
        <div className="text-xs text-gray-500 mb-1">Twoja wycena</div>
        <div className="text-3xl font-bold text-brand blur-md select-none">
          •••••••• – ••••••••&nbsp;PLN
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Numer telefonu</span>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+48 501 234 567"
            className="mt-1 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand outline-none disabled:bg-gray-50 disabled:text-gray-500"
            required
            disabled={subStep === "code"}
          />
        </label>

        <label className="block">
          <span className={`text-sm font-medium ${codeDisabled ? "text-gray-400" : "text-gray-700"}`}>
            Kod z SMS
          </span>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 8))}
            placeholder="Wprowadź kod tutaj po otrzymaniu SMS"
            className={`mt-1 w-full px-4 py-3 border rounded-lg outline-none ${
              codeDisabled
                ? "border-gray-200 bg-gray-50 text-gray-400 tracking-normal text-base"
                : "border-brand bg-white text-brand tracking-widest text-xl text-center font-semibold focus:ring-2 focus:ring-brand"
            }`}
            disabled={codeDisabled}
            autoFocus={subStep === "code"}
          />
        </label>

        <div className="space-y-3 text-sm text-gray-700">
          <label className="flex items-start gap-2 cursor-pointer font-medium">
            <input
              type="checkbox"
              checked={consentTerms && consentContact}
              onChange={(e) => {
                setConsentTerms(e.target.checked);
                setConsentContact(e.target.checked);
              }}
              className="mt-1"
              disabled={subStep === "code"}
            />
            <span>Zaznacz wszystko</span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={consentTerms}
              onChange={(e) => setConsentTerms(e.target.checked)}
              className="mt-1"
              disabled={subStep === "code"}
            />
            <span>
              Akceptuję{" "}
              <a href="/polityka-prywatnosci" target="_blank" rel="noreferrer" className="underline">
                Politykę prywatności
              </a>
              .
            </span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={consentContact}
              onChange={(e) => setConsentContact(e.target.checked)}
              className="mt-1"
              disabled={subStep === "code"}
            />
            <span>Wyrażam zgodę na kontakt telefoniczny w celu doprecyzowania wyceny.</span>
          </label>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 bg-brand text-white rounded-lg font-semibold hover:bg-brand-600 disabled:opacity-50 transition"
        >
          {loading
            ? subStep === "phone"
              ? "Wysyłam SMS…"
              : "Weryfikuję…"
            : subStep === "phone"
              ? "Wyślij kod SMS"
              : "Zweryfikuj i pokaż wycenę"}
        </button>

        {subStep === "code" && (
          <button
            type="button"
            onClick={() => {
              setSubStep("phone");
              setCode("");
              setError(null);
            }}
            className="w-full text-sm text-gray-500 hover:text-brand underline"
          >
            Zmień numer telefonu
          </button>
        )}
      </form>
    </div>
  );
}
